import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import api from "@/lib/api";
import MoodChart from "@/components/MoodChart";

const MOODS = [
    { key: "calm", label: "calm", color: "#8A9A86" },
    { key: "joyful", label: "joyful", color: "#9EC3E3" },
    { key: "tired", label: "tired", color: "#7A736C" },
    { key: "anxious", label: "anxious", color: "#4F86C6" },
    { key: "low", label: "low", color: "#6B7A87" },
    { key: "angry", label: "angry", color: "#D95D5D" },
];

export default function Journal() {
    const [mood, setMood] = useState(null);
    const [intensity, setIntensity] = useState(3);
    const [content, setContent] = useState("");
    const [entries, setEntries] = useState([]);
    const [moodHistory, setMoodHistory] = useState([]);
    const [busy, setBusy] = useState(false);
    const [query, setQuery] = useState("");
    const [moodFilter, setMoodFilter] = useState(null);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return entries.filter((e) => {
            if (moodFilter && e.mood !== moodFilter) return false;
            if (!q) return true;
            return (e.content || "").toLowerCase().includes(q);
        });
    }, [entries, query, moodFilter]);

    const load = async () => {
        try {
            const j = await api.get("/journals");
            setEntries(j.data);
            const m = await api.get("/moods");
            setMoodHistory(m.data);
        } catch {}
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!content.trim() && !mood) { toast.error("write something or pick a mood"); return; }
        setBusy(true);
        try {
            if (mood) {
                await api.post("/moods", { mood, intensity, note: content.slice(0, 200) });
            }
            if (content.trim()) {
                await api.post("/journals", { content, mood });
            }
            toast.success("saved.");
            setContent("");
            setMood(null);
            setIntensity(3);
            await load();
        } catch (err) {
            toast.error("couldn't save");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-white/70 border border-clayHover rounded-3xl p-5 sm:p-8 shadow-soft">
                <h1 className="font-heading text-3xl tracking-tight text-ink mb-2">how are you, really?</h1>
                <p className="text-inkSoft font-body mb-8">no one else is reading this.</p>

                <div className="mb-6">
                    <label className="block text-xs uppercase tracking-widest text-inkSoft mb-3">today's mood</label>
                    <div className="grid grid-cols-3 gap-3">
                        {MOODS.map(m => (
                            <button
                                key={m.key}
                                type="button"
                                data-testid={`mood-${m.key}`}
                                onClick={() => setMood(m.key)}
                                className={`px-4 py-3 rounded-2xl border transition-all font-body ${
                                    mood === m.key
                                        ? "border-transparent text-bone scale-[1.02]"
                                        : "border-clayHover text-ink hover:bg-clay"
                                }`}
                                style={mood === m.key ? { backgroundColor: m.color } : {}}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>
                </div>

                {mood && (
                    <div className="mb-6">
                        <label className="block text-xs uppercase tracking-widest text-inkSoft mb-3">intensity ({intensity})</label>
                        <input
                            data-testid="mood-intensity"
                            type="range"
                            min={1}
                            max={5}
                            value={intensity}
                            onChange={e => setIntensity(Number(e.target.value))}
                            className="w-full accent-terracotta"
                        />
                    </div>
                )}

                <form onSubmit={submit}>
                    <label className="block text-xs uppercase tracking-widest text-inkSoft mb-3">a few words</label>
                    <textarea
                        data-testid="journal-input"
                        value={content}
                        onChange={e => setContent(e.target.value)}
                        rows={6}
                        placeholder="today felt like…"
                        className="w-full bg-clay/40 border border-clayHover rounded-2xl p-4 outline-none focus:border-terracotta resize-none font-journal text-ink leading-relaxed"
                    />
                    <button
                        data-testid="journal-save-btn"
                        type="submit"
                        disabled={busy}
                        className="mt-5 bg-terracotta text-bone rounded-full px-7 py-3 hover:bg-terracottaHover transition-colors font-body disabled:opacity-60"
                    >
                        {busy ? "saving…" : "save entry"}
                    </button>
                </form>
            </div>

            <div className="bg-bone/40 rounded-3xl p-5 sm:p-8">
                <h2 className="font-heading text-2xl tracking-tight text-ink mb-2">your last 14 days</h2>
                <p className="text-inkSoft font-body mb-6 text-sm">a soft rhythm, not a verdict.</p>
                <MoodChart moods={moodHistory} />

                <h2 className="font-heading text-2xl tracking-tight text-ink mt-10 mb-4">past entries</h2>

                {entries.length > 0 && (
                    <div className="mb-5" data-testid="journal-filters">
                        <div className="relative mb-3">
                            <Search className="w-4 h-4 text-inkSoft absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="search your entries…"
                                className="w-full pl-9 pr-4 py-2.5 bg-white border border-clayHover rounded-full outline-none focus:border-terracotta font-body text-sm text-ink"
                                data-testid="journal-search"
                            />
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            <button
                                onClick={() => setMoodFilter(null)}
                                data-testid="journal-filter-all"
                                className={`text-xs px-3 py-1 rounded-full font-body transition-colors ${
                                    !moodFilter ? "bg-ink text-bone" : "bg-clay/60 text-inkSoft hover:text-ink"
                                }`}
                            >
                                all
                            </button>
                            {MOODS.map((m) => (
                                <button
                                    key={m.key}
                                    onClick={() => setMoodFilter(moodFilter === m.key ? null : m.key)}
                                    data-testid={`journal-filter-${m.key}`}
                                    className={`text-xs px-3 py-1 rounded-full font-body transition-colors ${
                                        moodFilter === m.key ? "text-bone" : "text-inkSoft hover:text-ink bg-clay/60"
                                    }`}
                                    style={moodFilter === m.key ? { backgroundColor: m.color } : {}}
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {entries.length === 0 && (
                    <p className="text-inkSoft font-body italic">your reflections will live here.</p>
                )}
                <div className="space-y-5" data-testid="journal-list">
                    {filtered.length === 0 && entries.length > 0 && (
                        <p className="text-inkSoft font-body italic text-sm">no entries match that filter.</p>
                    )}
                    {filtered.map(e => (
                        <div key={e.id} className="bg-white border border-clayHover rounded-2xl p-5 shadow-soft">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs uppercase tracking-widest text-inkSoft">
                                    {new Date(e.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                                </span>
                                {e.mood && (
                                    <span className="text-xs px-2 py-1 rounded-full bg-clay text-ink font-body">{e.mood}</span>
                                )}
                            </div>
                            <p className="font-journal text-ink leading-relaxed whitespace-pre-wrap">{e.content}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
